import apiClient from './api';
import {
  parseSentinel2ObservationResponse,
  type HeatMapObservationScene,
  type HeatMapSatelliteOption,
} from '../features/shared/components/HeatMapObservationBar';

const MEMORY = new Map<string, CacheEntry>();
const STORAGE_KEY = 'digisaka:observation-date-cache:v3';
const MAX_ENTRIES = 80;
/** Past windows are stable; keep for a day. */
const TTL_MS = 24 * 60 * 60 * 1000;
/** In-flight dedupe for the same key. */
const INFLIGHT = new Map<string, Promise<Sentinel2ObservationDateResponse>>();

export interface Sentinel2ObservationDateResponse {
  success?: boolean;
  dates?: string[];
  observations?: Array<{
    date: string;
    tile_cloud_percentage?: number | null;
    cloud_percentage?: number | null;
  }>;
  cloud_scope?: string;
  cloud_note?: string;
}

interface CacheEntry {
  savedAt: number;
  data: Sentinel2ObservationDateResponse;
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((v) => stableStringify(v)).join(',')}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

/** Short geometry fingerprint for cache keys (farm polygon + ids if present). */
export function geometryCacheKey(geometry: unknown): string {
  try {
    const raw = stableStringify(geometry);
    // FNV-1a 32-bit
    let h = 0x811c9dc5;
    for (let i = 0; i < raw.length; i++) {
      h ^= raw.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(36);
  } catch {
    return 'geom';
  }
}

export function observationDateCacheKey(
  geometry: unknown,
  startDate: string,
  endDate: string,
  satellite: string = 'S2'
): string {
  return `${geometryCacheKey(geometry)}|${satellite}|${startDate}|${endDate}`;
}

function readSessionStore(): Record<string, CacheEntry> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, CacheEntry>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSessionStore(store: Record<string, CacheEntry>) {
  try {
    const keys = Object.keys(store);
    if (keys.length > MAX_ENTRIES) {
      keys
        .map((k) => ({ k, t: store[k]?.savedAt ?? 0 }))
        .sort((a, b) => a.t - b.t)
        .slice(0, keys.length - MAX_ENTRIES)
        .forEach(({ k }) => {
          delete store[k];
        });
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // quota / private mode — ignore
  }
}

function getCached(key: string): Sentinel2ObservationDateResponse | null {
  const now = Date.now();
  const mem = MEMORY.get(key);
  if (mem && now - mem.savedAt < TTL_MS) {
    return mem.data;
  }

  const store = readSessionStore();
  const entry = store[key];
  if (entry && now - entry.savedAt < TTL_MS && entry.data) {
    MEMORY.set(key, entry);
    return entry.data;
  }
  return null;
}

/** Synchronous cache peek (memory + sessionStorage). */
export function peekSentinel2ObservationDatesCache(params: {
  geometry: unknown;
  start_date: string;
  end_date: string;
  satellite?: string;
}): Sentinel2ObservationDateResponse | null {
  return getCached(
    observationDateCacheKey(
      params.geometry,
      params.start_date,
      params.end_date,
      params.satellite ?? 'S2'
    )
  );
}

function setCached(key: string, data: Sentinel2ObservationDateResponse) {
  const entry: CacheEntry = { savedAt: Date.now(), data };
  MEMORY.set(key, entry);
  if (MEMORY.size > MAX_ENTRIES) {
    const oldest = MEMORY.keys().next().value;
    if (oldest) MEMORY.delete(oldest);
  }
  const store = readSessionStore();
  store[key] = entry;
  writeSessionStore(store);
}

/**
 * Fetch Sentinel-2 observation dates for a window, with memory + sessionStorage cache.
 * Identical in-flight requests for the same key share one network call.
 */
export async function fetchSentinel2ObservationDatesCached(params: {
  geometry: unknown;
  start_date: string;
  end_date: string;
  /** S2 | S1 | L8 — same /sentinel2observationdate/ endpoint */
  satellite?: string;
  /** When true, bypass cache and refresh. */
  force?: boolean;
}): Promise<Sentinel2ObservationDateResponse> {
  const satellite = (params.satellite || 'S2').toUpperCase();
  const key = observationDateCacheKey(
    params.geometry,
    params.start_date,
    params.end_date,
    satellite
  );

  if (!params.force) {
    const hit = getCached(key);
    if (hit) return hit;
  }

  const existing = INFLIGHT.get(key);
  if (existing) return existing;

  const promise = (async () => {
    const response = await apiClient.post<Sentinel2ObservationDateResponse>(
      '/sentinel2observationdate/',
      {
        geometry: params.geometry,
        start_date: params.start_date,
        end_date: params.end_date,
        satellite,
      }
    );
    const data = response.data ?? { success: true, dates: [], observations: [] };
    setCached(key, data);
    return data;
  })().finally(() => {
    INFLIGHT.delete(key);
  });

  INFLIGHT.set(key, promise);
  return promise;
}

/** Parse API/cache payload into observation bar scenes. */
export function scenesFromObservationDateResponse(
  data: Sentinel2ObservationDateResponse,
  satellite: HeatMapSatelliteOption = 'S2'
): HeatMapObservationScene[] {
  return parseSentinel2ObservationResponse(data, satellite);
}
